import 'chart.js/auto';
import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import Toast from '../../helpers/Toast';
import { KpiService } from '../../services/KpiService';
import { useMobile } from '../../components/Layout/UseResponsive';
import { Chart, registerables } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Col, Row } from 'reactstrap';
import ButtonComponent from '../../components/ButtonComponent';
import { IoArrowBackSharp } from 'react-icons/io5';
import Layout from '../../components/Layout/Layout';

interface AssistanceCounts {
    currentMonthAssistances: number | null;
    lastMonthAssistances: number | null;
}

interface TechnicianHours {
    technicianName: string | null;
    hoursWorked: number | null;
}

interface TechnicianWeeklyAssistance {
    technicianName: string | null;
    technicianCode: string | null;
    pastWeekCount: number | null;
    currentWeekCount: number | null;
}

interface WeekDates {
    startOfLastWeek: string | null;
    endOfLastWeek: string | null;
    startOfCurrentWeek: string | null;
    endOfCurrentWeek: string | null;
}

const KpiTechnicianDashboard: React.FC = () => {
    const intl = useIntl();
    const isMobile = useMobile();
    const navigate = useNavigate();
    const kpiService = new KpiService();

    const [assistanceCounts, setAssistanceCounts] = useState<AssistanceCounts | null>(null);
    const [technicianHours, setTechnicianHours] = useState<TechnicianHours[]>([]);
    const [chartData, setChartData] = useState<TechnicianWeeklyAssistance[]>([]);
    const [weekDates, setWeekDates] = useState<WeekDates | null>(null);

    const [currentChart, setCurrentChart] = useState<'registeredTimes' | 'debitedHours'>('registeredTimes');
    const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

    useEffect(() => {
        fetchChartData();
        startTimer();
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [currentChart]);

    const startTimer = () => {
        if (timer) clearInterval(timer);
        const newTimer = setInterval(() => {
            setCurrentChart(prevChart => prevChart === 'registeredTimes' ? 'debitedHours' : 'registeredTimes');
        }, 60000);
        setTimer(newTimer);
    };

    const handleButtonClick = (chartType: 'registeredTimes' | 'debitedHours') => {
        setCurrentChart(chartType);
        startTimer();
    };

    useEffect(() => {
        fetchAssistanceCounts();
        fetchTechnicianHours();
        fetchChartData();
    }, []);

    const fetchAssistanceCounts = async () => {
        try {
            const response = await kpiService.GetCurrentAndLastMonthAssistances();
            if (response.success && response.obj) {
                setAssistanceCounts(response.obj);
                return;
            }
            Toast.Show("error", response.message);
        } catch (error) {
            console.error("Erro ao obter o número de assistências. Método : fetchAssistanceCounts", error);
        }
    };

    const fetchTechnicianHours = async () => {
        try {
            const response = await kpiService.GetTechnicianHoursPastDay();
            if (response.success && response.obj) {
                setTechnicianHours(response.obj);
                return;
            }
            Toast.Show("error", response.message);
        } catch (error) {
            console.error("Erro ao obter os registos totais de ontem. Método : fetchTechnicianHours", error);
        }
    };

    const fetchChartData = async () => {
        try {
            const response = currentChart === "registeredTimes" ? await kpiService.GetCurrentAndLastWeekRegisteredTimesPerTechnician() : await kpiService.GetCurrentAndLastWeekDebtTimesPerTechnician();
            if (response.success && response.obj) {
                setChartData(response.obj.technicians || []);
                setWeekDates({
                    startOfLastWeek: response.obj.startOfLastWeek,
                    endOfLastWeek: response.obj.endOfLastWeek,
                    startOfCurrentWeek: response.obj.startOfCurrentWeek,
                    endOfCurrentWeek: response.obj.endOfCurrentWeek,
                });
                return;
            }
            Toast.Show("error", response.message);
        } catch (error) {
            console.error("Erro ao obter comparativo entre semanas dos tempos registados e/ou debitados. Método : fetchChartData ", error);
        }
    };

    const renderTechnicianHours = () => (
        <div>
            <h3><FormattedMessage id="Screens.Kpi.TechnicianDashboard.YesterdayTotalEntries" /></h3>
            <div style={{ maxHeight: '100%', overflowY: 'auto', fontSize: '14px' }}>
                {technicianHours.map((tech, index) => (
                    <div key={index}>{tech.technicianName} - {tech.hoursWorked} <FormattedMessage id="Screens.Kpi.TechnicianDashboard.Hours" /></div>
                ))}
            </div>
        </div>
    );

    const renderAssistanceCounts = () => (
        <div>
            <h3>
                <FormattedMessage id="Screens.Kpi.TechnicianDashboard.NumberOfAssistances" />
            </h3>
            <p style={{ fontSize: '16px' }}>
                <FormattedMessage id="Screens.Kpi.TechnicianDashboard.CurrentMonth" />: {assistanceCounts?.currentMonthAssistances}
            </p>
            <p style={{ fontSize: '16px' }}>
                <FormattedMessage id="Screens.Kpi.TechnicianDashboard.PastMonth" />: {assistanceCounts?.lastMonthAssistances}
            </p>
        </div>
    );


    function calculateStepSize(maxValue: any) {
        if (maxValue <= 1) return 0.1;
        if (maxValue <= 5) return 0.5;
        if (maxValue <= 10) return 1;
        if (maxValue <= 50) return 5;
        if (maxValue <= 100) return 10;
        return 20;
    }

    const maxDataValue = Math.max(...chartData.map(d => Math.max(d.pastWeekCount ?? 0, d.currentWeekCount ?? 0)));
    const stepSize = calculateStepSize(maxDataValue);
    const maxYValue = Math.ceil((maxDataValue + stepSize) / stepSize) * stepSize;

    Chart.register(...registerables, ChartDataLabels);
    const renderChart = () => {
        if (!weekDates) return <p>...</p>;

        const { startOfLastWeek, endOfLastWeek, startOfCurrentWeek, endOfCurrentWeek } = weekDates;

        const data = {
            labels: chartData.map((d) => d.technicianName || "N/A"),
            datasets: [
                {
                    label: intl.formatMessage({ id: "Screens.Kpi.TechnicianDashboard.LastWeek" }),
                    data: chartData.map((d) => d.pastWeekCount || 0),
                    backgroundColor: 'rgba(21,96,130,255)',
                },
                {
                    label: intl.formatMessage({ id: "Screens.Kpi.TechnicianDashboard.CurrentWeek" }),
                    data: chartData.map((d) => d.currentWeekCount || 0),
                    backgroundColor: 'rgba(233,113,50,255)',
                }
            ]
        };

        const chartOptions = {
            plugins: {
                legend: {
                    position: "bottom" as const,
                    labels: {
                        font: {
                            size: isMobile ? 12 : 25,
                            weight: 'bold' as const,
                        },
                    },
                },
                datalabels: {
                    anchor: 'end' as const,
                    align: 'top' as const,
                    formatter: (value: any, context: any) => {

                        return `${value}`;
                    },
                    font: {
                        size: isMobile ? 12 : 30,
                        weight: 'bold' as const,
                    },
                    color: 'black',
                    offset: -12,
                },
            },
            scales: {
                x: {
                    ticks: {
                        font: {
                            size: isMobile ? 12 : 25,
                            weight: 'bold' as const,
                        },
                    },
                },
                y: {
                    title: {
                        display: true,
                        text: intl.formatMessage({ id: "Screens.Kpi.TechnicianDashboard.HoursPerWeek" }),
                        font: {
                            size: isMobile ? 12 : 25
                        }
                    },
                    max: maxYValue,
                    ticks: {
                        stepSize: stepSize,
                    },
                },
            },
            onClick: (event: any, elements: any[]) => {
                if (elements.length > 0) {
                    const elementIndex = elements[0].index;
                    const datasetIndex = elements[0].datasetIndex;
                    const technicianData = chartData[elementIndex];

                    let startDate, endDate;
                    if (datasetIndex === 0) {
                        startDate = startOfLastWeek;
                        endDate = endOfLastWeek;
                    } else {
                        startDate = startOfCurrentWeek;
                        endDate = endOfCurrentWeek;
                    }

                    if (technicianData.technicianCode && startDate && endDate) {
                        navigate(
                            `/?technician=${technicianData.technicianCode}&initialDate=${startDate.split('T')[0]}&endDate=${endDate.split('T')[0]}`
                        );
                    }
                }
            },
        };

        return (
            <div>
                <h3>
                    {currentChart === "registeredTimes" ? (
                        <FormattedMessage id="Screens.Kpi.TechnicianDashboard.RegisteredTimesComparisonBetweenWeeks" />
                    ) : (
                        <FormattedMessage id="Screens.Kpi.TechnicianDashboard.DebtHoursComparisonBetweenWeeks" />
                    )}
                </h3>
                <Bar data={data} options={chartOptions} style={{ width: '100%', height: '50vh' }} />
            </div>
        );
    };

    const goBack = () => {
        navigate("/kpis");
    };

    return (
        <>
            <Layout>
                <div className="pageContainer">
                    <div className="pageHeader">
                        <Row style={{ margin: "0px", padding: "0px" }}>
                            <Col style={{ margin: "0px", padding: "0px" }}>
                                <span className="pageTitle">
                                    <IoArrowBackSharp className="pageTitleBackIcon" onClick={goBack} />
                                    <FormattedMessage id="Screens.Kpi.TechnicianDashboard.Title" />
                                </span>
                            </Col>
                        </Row>
                    </div>
                    <div className="pageBody" style={{ display: 'flex', flexDirection: 'row', gap: '20px', padding: '20px', flexWrap: 'wrap' }}>
                        <div style={{ textAlign: 'center', flex: '1', display: 'flex', flexDirection: 'column', gap: '20px', width: '20%' }}>
                            <div style={{ border: '1px solid #000', padding: '20px' }}>
                                {renderAssistanceCounts()}
                            </div>
                            <div style={{ border: '1px solid #000', padding: '20px' }}>
                                {renderTechnicianHours()}
                            </div>
                        </div>
                        <div style={{ textAlign: 'center', flex: '2', border: '1px solid #000', padding: '20px', width: '80%' }}>
                            {renderChart()}

                            <Row style={{ marginTop: "2%", justifyContent: "center", gap: "5%" }}>
                                <Col xl={3} lg={12} md={12} sm={12} xs={12}>
                                    <ButtonComponent
                                        disabled={false}
                                        text={intl.formatMessage({ id: "Screens.Kpi.TechnicianDashboard.RegisteredTimesComparisonBetweenWeeks.Button" })}
                                        onClick={() => handleButtonClick('registeredTimes')}
                                    />
                                </Col>

                                <Col xl={3} lg={12} md={12} sm={12} xs={12}>
                                    <ButtonComponent
                                        disabled={false}
                                        text={intl.formatMessage({ id: "Screens.Kpi.TechnicianDashboard.DebtHoursComparisonBetweenWeeks.Button" })}
                                        onClick={() => handleButtonClick('debitedHours')}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
};

export default KpiTechnicianDashboard;